import React from 'react';

import useEventTracking from '@hooks/useEventTracking';
import { titv_show_path } from '@routes';
import { ClickTarget, ContentType, TrackingLocation, click } from '@tracking';
import { formatUtcDate } from '@utils/dates';

import { SectionItem } from './types';

interface BlueprintTitvCardProps {
  item: SectionItem;
  onPlay?: (item: SectionItem) => void;
}

const BlueprintTitvCard = ({ item, onPlay }: BlueprintTitvCardProps) => {
  const { trackEvent } = useEventTracking({
    location: TrackingLocation.Titv,
    contentId: item.contentId || item.id,
    contentType: ContentType.TITVStream,
  });

  const guid = item.metadata?.guid;
  // Custom items have no TITV show to link to; if they carry a video URL, play it inline.
  const playable = !guid && Boolean(item.videoUrl);

  const trackClick = () =>
    trackEvent(
      click({
        clickTarget: ClickTarget.Card,
        clickText: item.title || undefined,
        contentId: item.contentId || item.id,
        contentType: ContentType.TITVStream,
      })
    );

  const cardBody = (
    <>
      {item.imageUrl && (
        <div className="relative aspect-[16/9] w-full overflow-hidden">
          <img
            src={item.imageUrl}
            alt={item.title || ''}
            className="block w-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
          {playable && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 transition-opacity group-hover:opacity-100">
              <div className="size-56 flex items-center justify-center rounded-full bg-white/90">
                <svg className="size-24 ml-4 text-black" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            </div>
          )}
        </div>
      )}
      <div className="flex flex-col gap-[2px]">
        {item.title && (
          <h3 className="m-0 font-suisse-works text-headline-xxs text-black">{item.title}</h3>
        )}
        {item.publishedDate && (
          <p className="m-0 font-suisse-screen text-disclosure-sm text-grayscale-charcoal">
            {formatUtcDate(item.publishedDate)}
          </p>
        )}
      </div>
    </>
  );

  if (playable) {
    return (
      <button
        type="button"
        onClick={() => onPlay?.(item)}
        onClickCapture={trackClick}
        className="group flex cursor-pointer flex-col gap-12 border-0 bg-transparent p-0 text-left"
      >
        {cardBody}
      </button>
    );
  }

  return (
    <a
      href={guid ? titv_show_path({ guid }) : '#'}
      className="group flex flex-col gap-12 no-underline"
      onClickCapture={trackClick}
    >
      {cardBody}
    </a>
  );
};

export default BlueprintTitvCard;
